import {useInfiniteQuery, useMutation, useQuery, useQueryClient, type InfiniteData} from '@tanstack/react-query';
import {GetMessagesResponse, Message, MessageAttachment, MessageQuote, GetEntityReferencesResponse, EntityReference, EntityReferenceOption, ItemEnvelope, ListEnvelope, ChatMessageReaction, toggleMessageReaction, isUnreadCountStaleAfterRead, type MessageSuggestedActionStatus} from '@ruta/shared';
import {makeApiCall} from '@/lib/api';
import {getConversations, GetConversationsResponse, ConversationFilters} from '@/lib/api/routes/conversations';
import {useToast} from '@/components/ui/use-toast';

/**
 * Session-scoped read receipts: customerId → ISO time we optimistically marked
 * the thread read. The inbox reads unread counts from an eventually-consistent
 * index and is refetched on any org-wide message broadcast, on window focus,
 * and on staleness — so a refetch landing before the read-reset propagates would
 * otherwise resurrect the unread badge on a thread the user already opened
 * ("shows red even though I didn't open the message"). We replay these receipts
 * over every conversations refetch to keep the read state durable, and drop the
 * receipt the moment a genuinely newer message arrives or the user re-marks the
 * thread unread. Module-level (not React state) so it survives remounts of the
 * messages surface, matching the useMessageDraft store pattern.
 */
const conversationReadReceipts = new Map<string, string>();

export function recordConversationRead(customerId: string): void {
  if (customerId) conversationReadReceipts.set(customerId, new Date().toISOString());
}

export function clearConversationReadReceipt(customerId: string): void {
  if (customerId) conversationReadReceipts.delete(customerId);
}

/**
 * Replay read receipts over a fetched conversations page so a stale unread count
 * from the eventually-consistent index can't flip an already-read thread back to
 * "unread". Zeroes the row's count when the receipt still applies and keeps the
 * page's totalUnread badge consistent with the visible rows.
 */
export function applyReadReceipts(data: InfiniteData<GetConversationsResponse>): InfiniteData<GetConversationsResponse> {
  if (conversationReadReceipts.size === 0) return data;
  let suppressed = 0;
  const pages = data.pages.map((page) => {
    let pageChanged = false;
    const conversations = page.conversations.map((c) => {
      const markedReadAt = conversationReadReceipts.get(c.customerId);
      if (
        markedReadAt &&
        isUnreadCountStaleAfterRead({
          unreadCount: c.unreadCount,
          lastMessageTime: c.lastMessageTime,
          markedReadAt,
        })
      ) {
        suppressed += 1;
        pageChanged = true;
        return { ...c, unreadCount: 0 };
      }
      return c;
    });
    return pageChanged ? { ...page, conversations } : page;
  });
  if (suppressed === 0) return data;
  // totalUnread is a global counter carried on the first page; keep the header
  // in step with the rows we just cleared (self-heals on the next server count).
  const [firstPage, ...restPages] = pages;
  return {
    ...data,
    pages: [
      { ...firstPage, totalUnread: Math.max(0, firstPage.totalUnread - suppressed) },
      ...restPages,
    ],
  };
}

// Query keys for TanStack Query
export const messageKeys = {
  all: ['messages'] as const,
  lists: () => [...messageKeys.all, 'list'] as const,
  list: (customerId: string) => [...messageKeys.lists(), customerId] as const,
  conversations: (filters?: ConversationFilters) =>
    filters ? ([...messageKeys.all, 'conversations', filters] as const)
            : ([...messageKeys.all, 'conversations'] as const),
  entityReferences: (customerId: string, query?: string, limit = 10) =>
    [...messageKeys.all, 'entity-references', customerId, query, limit] as const,
};

// Hook for loading messages for a specific customer
export function useMessages(customerId: string) {
  return useQuery({
    queryKey: messageKeys.list(customerId),
    queryFn: async (): Promise<GetMessagesResponse> => {
      const response = await makeApiCall<ListEnvelope<Message>>('GET', `/customers/${customerId}/messages`);
      return {
        messages: response.data,
        hasMore: response.pagination.hasMore,
        nextToken: response.pagination.nextCursor ?? undefined,
      };
    },
    enabled: !!customerId,
    staleTime: 1000 * 30, // 30 seconds - messages should be relatively fresh
    gcTime: 1000 * 60 * 5, // 5 minutes
  });
}

/**
 * Toggle a tapback/reaction on a customer message. Optimistically updates the
 * cached thread so the chip appears/disappears instantly, then reconciles on
 * settle. currentUserId is the staff cognitoSub, which matches the userId the
 * API stores on each reaction.
 */
export function useToggleMessageReaction(
  customerId: string,
  currentUserId: string,
  currentUserName: string,
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ messageId, messageTimestamp, emoji }: { messageId: string; messageTimestamp: string; emoji: string }) =>
      makeApiCall<ItemEnvelope<{ messageId: string; reactions: ChatMessageReaction[] }>>(
        'PUT',
        `/customers/${customerId}/message-reactions`,
        { messageId, messageTimestamp, emoji },
      ),
    onMutate: async ({ messageId, emoji }) => {
      const key = messageKeys.list(customerId);
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData<GetMessagesResponse>(key);

      queryClient.setQueryData<GetMessagesResponse>(key, (old) => {
        if (!old) return old;
        return {
          ...old,
          messages: old.messages.map((m) =>
            m.messageId === messageId
              ? {
                  ...m,
                  reactions: toggleMessageReaction(m.reactions, {
                    emoji,
                    userId: currentUserId,
                    userName: currentUserName,
                    reactedAt: new Date().toISOString(),
                  }),
                }
              : m,
          ),
        };
      });

      return { previous };
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) {
        queryClient.setQueryData(messageKeys.list(customerId), context.previous);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: messageKeys.list(customerId) });
    },
  });
}

/**
 * Unsend a message the caller sent (RUT-2909). Optimistically tombstones the
 * cached message (unsentAt + cleared content/attachments) so the bubble flips
 * to the "unsent" placeholder instantly, then reconciles on settle. Only the
 * in-app copy is recalled — an SMS/email nudge already sent stays out.
 */
export function useUnsendCustomerMessage(customerId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ messageId, messageTimestamp }: { messageId: string; messageTimestamp: string }) =>
      makeApiCall<ItemEnvelope<{ messageId: string; unsentAt: string }>>(
        'PATCH',
        `/customers/${customerId}/messages`,
        { unsendMessageId: messageId, unsendMessageTimestamp: messageTimestamp },
      ),
    onMutate: async ({ messageId }) => {
      const key = messageKeys.list(customerId);
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData<GetMessagesResponse>(key);

      queryClient.setQueryData<GetMessagesResponse>(key, (old) => {
        if (!old) return old;
        return {
          ...old,
          messages: old.messages.map((m) =>
            m.messageId === messageId
              ? {
                  ...m,
                  unsentAt: new Date().toISOString(),
                  content: '',
                  attachments: undefined,
                  entityReferences: undefined,
                  reactions: undefined,
                }
              : m,
          ),
        };
      });

      return { previous };
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) {
        queryClient.setQueryData(messageKeys.list(customerId), context.previous);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: messageKeys.list(customerId) });
      queryClient.invalidateQueries({ queryKey: messageKeys.conversations() });
    },
  });
}

/**
 * Edit a message the caller sent (RUT-2648). Optimistically rewrites the cached
 * message's content and stamps editedAt so the bubble shows the new text with
 * an "(edited)" marker instantly, then reconciles on settle. Only the in-app
 * copy is rewritten — an SMS/email nudge already sent stays as delivered.
 */
export function useEditCustomerMessage(customerId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      messageId,
      messageTimestamp,
      content,
    }: {
      messageId: string;
      messageTimestamp: string;
      content: string;
    }) =>
      makeApiCall<ItemEnvelope<{ messageId: string; content: string; editedAt: string }>>(
        'PATCH',
        `/customers/${customerId}/messages`,
        { editMessageId: messageId, editMessageTimestamp: messageTimestamp, editContent: content },
      ),
    onMutate: async ({ messageId, content }) => {
      const key = messageKeys.list(customerId);
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData<GetMessagesResponse>(key);

      queryClient.setQueryData<GetMessagesResponse>(key, (old) => {
        if (!old) return old;
        return {
          ...old,
          messages: old.messages.map((m) =>
            m.messageId === messageId
              ? { ...m, content, editedAt: new Date().toISOString() }
              : m,
          ),
        };
      });

      return { previous };
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) {
        queryClient.setQueryData(messageKeys.list(customerId), context.previous);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: messageKeys.list(customerId) });
      queryClient.invalidateQueries({ queryKey: messageKeys.conversations() });
    },
  });
}

// Hook for sending messages as admin
export function useSendMessage(customerId: string) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (params: { content: string; entityReferences?: EntityReference[]; attachments?: MessageAttachment[]; includeLoginLink?: boolean; scheduledFor?: string; quote?: MessageQuote }): Promise<{ message: Message }> => {
      const res = await makeApiCall<ItemEnvelope<{ message: Message }>>('POST', `/customers/${customerId}/messages`, params);
      return res.data;
    },
    onMutate: async (params) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: messageKeys.list(customerId) });

      // Snapshot the previous value
      const previousMessages = queryClient.getQueryData<GetMessagesResponse>(messageKeys.list(customerId));

      // A scheduled message isn't delivered yet, so don't drop it into the live
      // thread — the scheduled tray reads it after the refetch (RUT-2256).
      if (params.scheduledFor) {
        return { previousMessages };
      }

      // Optimistically update to the new value
      if (previousMessages) {
        const optimisticMessage: Message = {
          messageId: `temp-${Date.now()}`,
          conversationId: '',
          orgId: '',
          customerId,
          sender: 'admin' as const,
          senderName: 'Admin',
          senderId: '',
          content: params.content,
          entityReferences: params.entityReferences,
          attachments: params.attachments,
          includeLoginLink: params.includeLoginLink,
          quote: params.quote,
          timestamp: new Date().toISOString(),
          read: false,
        };

        queryClient.setQueryData<GetMessagesResponse>(messageKeys.list(customerId), {
          ...previousMessages,
          messages: [optimisticMessage, ...previousMessages.messages],
        });
      }

      // Return a context object with the snapshotted value
      return { previousMessages };
    },
    onError: (err, variables, context) => {
      // If the mutation fails, use the context returned from onMutate to roll back
      if (context?.previousMessages) {
        queryClient.setQueryData(messageKeys.list(customerId), context.previousMessages);
      }

      console.error('Failed to send message:', err);
      toast({
        title: 'Failed to send message',
        description: 'Please try again later.',
        variant: 'destructive',
      });
    },
    onSuccess: (_data, variables) => {
      // Invalidate and refetch messages to ensure we have the latest data
      queryClient.invalidateQueries({ queryKey: messageKeys.list(customerId) });

      if (variables.scheduledFor) {
        toast({
          title: 'Message scheduled',
          description: `It will send ${formatScheduledFor(variables.scheduledFor)}.`,
        });
        return;
      }

      toast({
        title: 'Message sent',
        description: 'Your message has been sent to the customer.',
      });
    },
  });
}

/** Human-readable "when it sends" line for scheduled-message toasts. */
function formatScheduledFor(iso: string): string {
  const date = new Date(iso);
  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

/**
 * Edit a scheduled (not-yet-sent) message before it delivers (RUT-2256).
 * Rides the existing PATCH /customers/{id}/messages route.
 */
export function useUpdateScheduledMessage(customerId: string) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (params: {
      scheduledMessageId: string;
      scheduledMessageTimestamp: string;
      content?: string;
      entityReferences?: EntityReference[];
      attachments?: MessageAttachment[];
      includeLoginLink?: boolean;
      scheduledFor?: string;
    }) => {
      const res = await makeApiCall<ItemEnvelope<{ message: Message }>>('PATCH', `/customers/${customerId}/messages`, params);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: messageKeys.list(customerId) });
      toast({ title: 'Scheduled message updated' });
    },
    onError: (err) => {
      console.error('Failed to update scheduled message:', err);
      toast({ title: 'Could not update scheduled message', description: 'Please try again.', variant: 'destructive' });
    },
  });
}

/**
 * Cancel a scheduled message before it sends (RUT-2256). Optimistically drops
 * it from the cached thread so the tray updates instantly.
 */
export function useCancelScheduledMessage(customerId: string) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (params: { scheduledMessageId: string; scheduledMessageTimestamp: string }) => {
      return await makeApiCall('PATCH', `/customers/${customerId}/messages`, { ...params, cancel: true });
    },
    onMutate: async ({ scheduledMessageId }) => {
      await queryClient.cancelQueries({ queryKey: messageKeys.list(customerId) });
      const previous = queryClient.getQueryData<GetMessagesResponse>(messageKeys.list(customerId));
      queryClient.setQueryData<GetMessagesResponse>(messageKeys.list(customerId), (old) =>
        old ? { ...old, messages: old.messages.filter((m) => m.messageId !== scheduledMessageId) } : old,
      );
      return { previous };
    },
    onError: (err, _vars, context) => {
      if (context?.previous) {
        queryClient.setQueryData(messageKeys.list(customerId), context.previous);
      }
      console.error('Failed to cancel scheduled message:', err);
      toast({ title: 'Could not cancel message', description: 'Please try again.', variant: 'destructive' });
    },
    onSuccess: () => {
      toast({ title: 'Scheduled message canceled' });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: messageKeys.list(customerId) });
    },
  });
}

/** Dismiss or hand off a suggested action inferred from a customer message. */
export function useUpdateSuggestedAction(customerId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: {
      messageId: string;
      messageTimestamp: string;
      actionId: string;
      status: Extract<MessageSuggestedActionStatus, 'dismissed' | 'opened'>;
    }) => {
      const res = await makeApiCall<ItemEnvelope<{ success: true }>>(
        'PATCH',
        `/customers/${customerId}/messages`,
        {
          suggestedActionMessageId: params.messageId,
          suggestedActionMessageTimestamp: params.messageTimestamp,
          suggestedActionId: params.actionId,
          suggestedActionStatus: params.status,
        },
      );
      return res.data;
    },
    onMutate: async ({ messageId, actionId, status }) => {
      await queryClient.cancelQueries({ queryKey: messageKeys.list(customerId) });
      const previous = queryClient.getQueryData<GetMessagesResponse>(messageKeys.list(customerId));
      queryClient.setQueryData<GetMessagesResponse>(messageKeys.list(customerId), (old) => {
        if (!old) return old;
        return {
          ...old,
          messages: old.messages.map((m) =>
            m.messageId !== messageId
              ? m
              : {
                  ...m,
                  suggestedActions: (m.suggestedActions ?? []).map((a) =>
                    a.id === actionId ? { ...a, status } : a,
                  ),
                },
          ),
        };
      });
      return { previous };
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) {
        queryClient.setQueryData(messageKeys.list(customerId), context.previous);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: messageKeys.list(customerId) });
    },
  });
}

// Hook for loading conversations list (admin only) with infinite scroll.
// Filters are part of the cache key, so each combination caches separately.
export function useConversations(filters: ConversationFilters = {}) {
  return useInfiniteQuery<GetConversationsResponse>({
    queryKey: messageKeys.conversations(filters),
    queryFn: ({ pageParam }) =>
      getConversations({ limit: 50, nextToken: pageParam as string | undefined, ...filters }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.hasMore ? lastPage.nextToken : undefined,
    // Replay session read receipts so an eventually-consistent unread count from
    // a background refetch can't resurrect a thread the user already read.
    select: applyReadReceipts,
    staleTime: 1000 * 60, // 1 minute
    gcTime: 1000 * 60 * 10, // 10 minutes
  });
}

// Hook for marking messages as read
export function useMarkMessagesAsRead(customerId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const res = await makeApiCall<ItemEnvelope<{ success: true }>>('PATCH', `/customers/${customerId}/messages`, { read: true });
      return res.data;
    },
    // Apply the cache update synchronously when the mutation starts — not in
    // onSuccess. The PATCH round-trip is 100-500ms; if we waited for it, the
    // badge would stay visible during the lag and feel broken on first click.
    // The conversations list reads from GSI2 (eventually consistent), so we
    // also skip invalidating — the next natural refetch reconciles once the
    // GSI replicates the write.
    onMutate: () => {
      // Remember this read so a stale refetch from the eventually-consistent
      // index can't flip the thread back to unread before the reset propagates.
      recordConversationRead(customerId);
      const previous = queryClient.getQueriesData<InfiniteData<GetConversationsResponse>>({
        queryKey: messageKeys.conversations(),
      });
      queryClient.setQueriesData<InfiniteData<GetConversationsResponse>>(
        { queryKey: messageKeys.conversations() },
        (old) => {
          if (!old) return old;
          return {
            ...old,
            pages: old.pages.map((page, i) => {
              let decremented = false;
              const conversations = page.conversations.map(c => {
                if (c.customerId === customerId && c.unreadCount > 0) {
                  decremented = true;
                  return { ...c, unreadCount: 0 };
                }
                return c;
              });
              return {
                ...page,
                conversations,
                totalUnread: i === 0 && decremented
                  ? Math.max(0, page.totalUnread - 1)
                  : page.totalUnread,
              };
            }),
          };
        },
      );
      return { previous };
    },
    onError: (_err, _vars, context) => {
      // Roll back if the PATCH ends up failing — including the read receipt, so
      // a failed read doesn't keep suppressing the thread's real unread count.
      clearConversationReadReceipt(customerId);
      if (context?.previous) {
        for (const [key, data] of context.previous) {
          queryClient.setQueryData(key, data);
        }
      }
    },
  });
}

// Hook for marking a conversation as unread again (Gmail-style bulk action).
// Not bound to a specific customer — call mutateAsync(customerId) per selection.
export function useMarkConversationAsUnread() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (customerId: string) => {
      return await makeApiCall('PATCH', `/customers/${customerId}/messages`, { unread: true });
    },
    // Bump the cache synchronously so the unread badge appears the instant
    // the user clicks "Mark unread," not after the PATCH round-trip lands.
    onMutate: (customerId) => {
      // Deliberately re-marking unread — drop any read receipt so the guard
      // stops suppressing this thread's unread count.
      clearConversationReadReceipt(customerId);
      const previous = queryClient.getQueriesData<InfiniteData<GetConversationsResponse>>({
        queryKey: messageKeys.conversations(),
      });
      queryClient.setQueriesData<InfiniteData<GetConversationsResponse>>(
        { queryKey: messageKeys.conversations() },
        (old) => {
          if (!old) return old;
          return {
            ...old,
            pages: old.pages.map((page, i) => {
              let bumped = false;
              const conversations = page.conversations.map(c => {
                if (c.customerId === customerId) {
                  if (c.unreadCount === 0) bumped = true;
                  return { ...c, unreadCount: Math.max(1, c.unreadCount) };
                }
                return c;
              });
              return {
                ...page,
                conversations,
                totalUnread: i === 0 && bumped ? page.totalUnread + 1 : page.totalUnread,
              };
            }),
          };
        },
      );
      return { previous };
    },
    onError: (err, _vars, context) => {
      console.error('Failed to mark conversation as unread:', err);
      if (context?.previous) {
        for (const [key, data] of context.previous) {
          queryClient.setQueryData(key, data);
        }
      }
      toast({
        title: 'Could not mark unread',
        description: 'Please try again.',
        variant: 'destructive',
      });
    },
  });
}

// Hook for fetching entity references for autocomplete (admin)
export function useEntityReferences(
  customerId: string,
  query?: string,
  enabled = true,
  limit = 10,
) {
  return useQuery({
    queryKey: messageKeys.entityReferences(customerId, query, limit),
    queryFn: async (): Promise<GetEntityReferencesResponse> => {
      const params = new URLSearchParams();
      if (query) params.append('query', query);
      params.append('limit', String(limit));

      const url = `/customers/${customerId}/entity-references?${params.toString()}`;
      const res = await makeApiCall<ListEnvelope<EntityReferenceOption>>('GET', url);
      return { options: res.data };
    },
    enabled: enabled && !!customerId,
    staleTime: 1000 * 60, // 1 minute - autocomplete results can be cached briefly
    gcTime: 1000 * 60 * 5, // 5 minutes
  });
}
